<template>
  <div>
    <v-dialog v-model="dialog" @click:outside="onBack" content-class="insp-dialog-class">
      <v-card-title class="close-btn-container d-flex justify-end">
        <v-btn class="close-btn" icon @click="onBack">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <ReadinessCheckListView
        :proficiencyLevel="currentData?.data?.importanceLevel"
        :badge="currentData?.data?.badge"
        :responsibility="{
          id: currentData?.data?.responsibility_id,
          code: extractCode(currentData?.data?.code),
          name: currentData?.data?.name,
          type: currentData?.data?.type,
        }"
        :checkPoints="currentData?.checkpointsData"
        :checkpointCounts="currentData?.data?.checkpointCounts"
        :availableInspectors="currentData?.data?.avatars"
        :selectedItem="selectedItemTab"
      />
    </v-dialog>
    <div>
      <SharedTableSkeleton
        v-if="showSkeletonLoading"
        :row-count="pagination.perPage || 5"
        :column-count="5"
        column-template="2fr 150px 100px 1fr 140px"
      />
      <div v-if="!loading.initial && dataSource.length === 0">
        <NoRecordFound
          :title="''"
          :subtitle="''"
          showSpecificMessage="You're Not Assigned to a Position Yet"
          message=""
          class="no-record-found"
          :shouldShowAddAction="false"
          :noBorder="true"
          :showActiveCounter="false"
          :showClearAction="false"
          btnTitle=""
          description="Your readiness progress is linked to a position. Once assigned, you'll see your positions, roles and responsibilities."
        />
      </div>
      <div v-if="!loading.initial && dataSource.length > 0">
        <div class="employee-path">
          <div class="as-employee-legacy-tree" :class="{ 'flag-on': ideaMDP450 }">
            <PathToReadinessTreeList
              @optionClick="onOptionClick"
              @avatarOptionClick="onAvatarOptionClick"
              @treeExpandRole="onTreeExpandRole"
              @onExpansionStateChange="handleExpansionStateChange"
              :dataSource="dataSource"
              :topLevel="topLevel"
              :removeBorder="removeBorder"
              :dropdownItems="dropdownItems"
              :pathToReadinessColumnName="pathToReadinessColumnName"
              :availableCoachesColumnName="availableCoachesColumnName"
              :showInspectionBadge="true"
              :showDropDown="false"
              :showRespBadge="true"
              :showAvailableCoaches="true"
              :showImportanceSelector="false"
              :myStuffTreeMode="true"
              :autoExpandAll="false"
              :showInspectionButtons="false"
              :showPrepareButton="true"
              :expandedKeys="expandedKeys"
            />
          </div>
          <ExPagination
            v-if="dataSource.length > 0 && pagination.total > pagination.perPage"
            :pagination="pagination"
            @onRequest="handlePaginationRequest"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import PathToReadinessTreeList from "@components/DataTable/PathToReadinessTreeList.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import ReadinessCheckListView from "../views/ReadinessCheckListView/ReadinessCheckListView.vue";
import {
  getEmployeeAssignments,
  recalculateEmployeeReadiness,
  getListActiveCheckpointsEval,
  getRoleAssignment,
  getResponsibilityAssignment,
  getDirectResponsibilityAssignment,
} from "@/api";
import { useEmployeeStore } from "@/store/pinia/employee";
import { getStaticPathToReadinessSample } from "./pathToReadinessStaticSample";
import { resolveStoredAuthEmployeeId } from "@components/utils/authEmployeeContext";
import {
  LD_FLAG_IDEA_MDP_560,
  LD_FLAG_IDEA_MDP_585,
} from "@components/constants/launchDarklyFlagKeys";
export default {
  components: {
    PathToReadinessTreeList,
    ReadinessCheckListView,
    NoRecordFound,
    ExPagination,
    SharedTableSkeleton,
  },
  async mounted() {
    this.$loader.show();

    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));

    // idea-MDP-585 is the rollout gate for direct Responsibility → Position
    // attachment. The self-service tree only renders direct-attached
    // Responsibilities as Position-level siblings (Rule 12, ER-11414) when the
    // flag is on; off → legacy role-only tree, byte-identical to pre-ER-11414.
    try {
      this.directResponsibilityTreeEnabled =
        (await this.$getFlag(LD_FLAG_IDEA_MDP_585, false)) === true;
    } catch (error) {
      this.directResponsibilityTreeEnabled = false;
    }

    // ER-10613 / MDP-560 — consistent skeleton loading. The shared table skeleton
    // replaces the initial-load blank gap only when the audit rollout flag is on;
    // off → legacy blank gap, byte-identical to before this ticket.
    try {
      this.skeletonLoadingEnabled = (await this.$getFlag(LD_FLAG_IDEA_MDP_560, false)) === true;
    } catch (error) {
      this.skeletonLoadingEnabled = false;
    }

    this.clearModalStates();

    try {
      await this.getEmployeesAssignments();
      await this.getAuthUserReadiness();
    } catch (error) {
      console.error("Error during component initialization:", error);
    } finally {
      this.loading.initial = false;
      this.$loader.hide();
    }
  },
  computed: {
    refactoredAsEmployeePage() {
      return this.$store.getters["featureFlags/ideaMDP378"];
    },
    skipRecalculateReadinessOnSessionLoad() {
      return this.$store.getters["featureFlags/ideaMDP441"];
    },
    /** ER-8962 — gates the new YourPathToReadinessTree behind LD flag idea-MDP-450. */
    ideaMDP450() {
      return this.$store.getters["featureFlags/ideaMDP450"];
    },
    pathToReadinessColumnName() {
      return this.ideaMDP450 ? "Position/Role/ Responsibility" : "Assignments";
    },
    availableCoachesColumnName() {
      return this.ideaMDP450 ? "Coaches" : "Available Inspectors";
    },
    /** ER-10613 — parent-fed initial-load gate only shows the skeleton once idea-MDP-560 resolves true. */
    showSkeletonLoading() {
      return this.loading.initial && this.skeletonLoadingEnabled;
    },
  },
  data() {
    return {
      menuItems: [{ label: "Your Path to Readiness", enabled: true }],
      /** Set false to load assignments from the API instead of demo rows */
      useStaticPathToReadinessData: false,
      /** idea-MDP-585 — gates direct-attached Responsibility sibling rows (ER-11414). */
      directResponsibilityTreeEnabled: false,
      /** idea-MDP-560 — gates the initial-load table skeleton (ER-10613). */
      skeletonLoadingEnabled: false,
      dataSource: [],
      accountID: null,
      topLevel: "position",
      removeBorder: false,
      allResponsibilities: [],
      isForceBadge: false,
      showEmployeeIssueBadge: false,
      currentResponsibility: null,
      isInspectResModal: false,
      checkpointsData: [],
      evalModalEmpState: null,
      evalModalHeaderState: null,
      loading: {
        initial: true,
        readinessBar: false,
        responsibilities: {},
        assignments: false,
        forceBadge: false,
        employeeStatus: false,
        checkpoint: false,
        pagination: false,
        modal: false,
      },
      dialog: false,
      currentData: null,
      selectedItemTab: null,
      selectedTab: 0,
      currentReadiness: null,
      pagination: {
        limit: 10,
        offset: 0,
        total: 0,
        perPage: 10,
        page: 1,
      },
      // Add expanded row loading states
      expandedRowLoadingStates: {},
      // Store expanded tree state
      expandedKeys: [],
      // Store loaded child data to prevent duplicate API calls during expansion
      loadedChildData: new Map(),
    };
  },
  mixins: [dropdownMixin],
  watch: {
    "$route.query.employeeId": {
      async handler() {
        this.$loader.show();
        try {
          await this.getEmployeesAssignments();
          await this.getAuthUserReadiness();
        } catch (error) {
          console.error("Error reloading employee data:", error);
        } finally {
          this.$loader.hide();
        }
      },
    },
  },
  beforeDestroy() {
    // Clean up modal states when component is destroyed
    this.clearModalStates();
  },
  methods: {
    async getEmployeesAssignments() {
      try {
        this.loading.assignments = true;
        if (this.useStaticPathToReadinessData) {
          const sample = getStaticPathToReadinessSample();
          this.dataSource = sample.dataSource;
          this.expandedKeys = sample.expandedKeys;
          this.pagination.total = sample.paginationTotal;
          return;
        }
        const empId = resolveStoredAuthEmployeeId(this.$route.query.employeeId);
        if (empId == null) {
          this.dataSource = [];
          this.pagination.total = 0;
          return;
        }
        const { data } = await getEmployeeAssignments(
          this.accountID,
          empId,
          this.pagination.limit,
          this.pagination.offset,
          "active"
        );

        if (data) {
          // Update pagination data
          this.pagination.total = data.count || 0;

          if (data.results?.length > 0) {
            this.dataSource = this.transformData(data.results);
            console.log("Path to Readiness Data (dataSource):", this.dataSource);

            // Automatically load first level children (roles) for each position
            // to show parent and child both as in image prompts
            const accountID = this.accountID;
            for (const pos of data.results) {
              // A Position also expands when it carries direct-attached
              // Responsibility Assignments (Rule 12) — even with no Role rows.
              // Only under the idea-MDP-585 gate (ER-11414).
              const posDirectCount =
                pos.direct_responsibility_assignments_count ??
                pos.direct_responsibility_assignments?.length ??
                0;
              const expandDirectOnly = this.directResponsibilityTreeEnabled && posDirectCount > 0;
              if (pos.role_assignment_count > 0 || expandDirectOnly) {
                this.handlePositionExpansion(accountID, empId, pos.id).then(() => {
                  // After roles are loaded, load responsibilities for each role
                  const roles = this.dataSource.filter(
                    (item) => item.parentId === `position_${pos.id}`
                  );
                  for (const role of roles) {
                    if (role.data?.hasResponsibilities) {
                      this.handleRoleExpansion(accountID, empId, role.assignment_id, role);
                    }
                  }
                });
              }
            }
          } else {
            this.dataSource = [];
          }
        } else {
          this.dataSource = [];
          this.pagination.total = 0;
        }
      } catch (error) {
        console.error("Error fetching employee assignments:", error);
        this.dataSource = [];
        this.pagination.total = 0;
      } finally {
        this.loading.assignments = false;
      }
    },

    async getAuthUserReadiness() {
      try {
        this.loading.readinessBar = true;
        const empId = resolveStoredAuthEmployeeId(this.$route.query.employeeId);
        if (empId == null) {
          this.currentReadiness = null;
          return;
        }
        if (this.skipRecalculateReadinessOnSessionLoad) {
          const employeeStore = useEmployeeStore();
          const employeeData = await employeeStore.fetchCurrentEmployee(empId, true);
          this.currentReadiness = employeeData;
          return;
        }
        const val = false;
        const { data } = await recalculateEmployeeReadiness(this.accountID, empId, val);
        this.currentReadiness = data;
        console.log("Auth User Readiness (currentReadiness):", this.currentReadiness);
      } catch (error) {
        console.error("Error fetching employee readiness:", error);
        this.currentReadiness = null;
      } finally {
        this.loading.readinessBar = false;
      }
    },

    async fetchResponsibilityCheckpoints(responsibility) {
      const empId = resolveStoredAuthEmployeeId(this.$route.query.employeeId);
      if (empId == null) {
        return [];
      }
      try {
        this.loading.checkpoint = true;
        let checkpointsData = await this.loadAllCheckpointsEvaluationData(
          this.accountID,
          empId,
          responsibility.responsibility_id
        );

        if (checkpointsData && checkpointsData.length > 0) {
          checkpointsData = checkpointsData.map((el) => ({
            id: el.id,
            evaluation_status: el.evaluation_status,
            proficiency_level: el.proficiency_level,
            status: el.status,
            responsibility: el.responsibility,
          }));

          return checkpointsData;
        } else {
          return [];
        }
      } catch (error) {
        console.error(
          `Error loading checkpoints for responsibility ${responsibility.responsibility_id}:`,
          error
        );
        return [];
      } finally {
        this.loading.checkpoint = false;
      }
    },

    async loadAllCheckpointsEvaluationData(accountId, employeeId, responsibilityId) {
      try {
        const response = await getListActiveCheckpointsEval(
          accountId,
          responsibilityId,
          employeeId
        );
        return response.data;
      } catch (error) {
        console.error("Error loading checkpoints evaluation data:", error);
        return [];
      }
    },

    transformData(dataArray) {
      if (!Array.isArray(dataArray)) {
        console.warn("transformData received non-array data:", dataArray);
        return [];
      }

      const result = [];
      const today = new Date();

      dataArray.forEach((data) => {
        if (!data || !data.id) {
          console.warn("Invalid data item in transformData:", data);
          return;
        }

        // Transform position data
        const positionObject = this.transformPositionData(data, today);
        result.push(positionObject);

        // TODO: Later - handle role assignments when API is added
        // if (data.role_assignment_count > 0) {
        //   // Will call roles API and process role_assignments
        // }
      });

      // Sort the results by commitment level
      const commitmentOrder = {
        Primary: 1,
        "Preparing for": 2,
        Backup: 3,
      };

      result.sort((a, b) => {
        const aCommit = a.data?.commitment || "";
        const bCommit = b.data?.commitment || "";
        return (commitmentOrder[aCommit] || 99) - (commitmentOrder[bCommit] || 99);
      });

      return result;
    },

    transformPositionData(data, today) {
      const isNew =
        data.assigned_on &&
        new Date(data.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      // Direct-attached Responsibility Assignments (Rule 12 — ER-11412/ER-11413):
      // the position-listing payload may carry a count (or the rows themselves)
      // alongside role_assignment_count. Read defensively so a pre-ER-11412 API
      // response behaves exactly as before. The whole direct-sibling surface is
      // gated behind idea-MDP-585 (ER-11414): when off, counts stay 0 and the
      // tree renders the legacy role-only shape.
      const directResponsibilityCount = this.directResponsibilityTreeEnabled
        ? data.direct_responsibility_assignments_count ??
          data.direct_responsibility_assignments?.length ??
          0
        : 0;

      return {
        id: `position_${data.id}`, // Make position IDs unique
        parentId: -1, // Root-level has no parent
        data: {
          type: data.assignment_type || "position",
          commitment: data.commitment_level || "",
          assignmentType: data.assignment_type || "position",
          importanceLevel: data.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: this.extractCode(data.position_code) || "",
          name: data.position_name || "",
          isNew: isNew || false,
          isAccepted: false,
          acceptanceStatus: "pending",
          // Add expandable indicator if roles exist
          hasRoles: data.role_assignment_count > 0,
          roleCount: data.role_assignment_count,
          // Direct responsibilities make the Position expandable too, so rows
          // with only direct children (no Role rows) still render + expand.
          hasDirectResponsibilities: directResponsibilityCount > 0,
          directResponsibilityCount,
        },
        assignment_id: data.id,
        status: data.status || "active",
        readiness: data.current_readiness || 0,
      };
    },

    extractCode(str) {
      try {
        if (!str || typeof str !== "string") {
          return null;
        }
        let match = str.match(/#\d+/);
        return match ? match[0] : null;
      } catch (error) {
        console.error("Error extracting code from string:", str, error);
        return null;
      }
    },

    dropdownItems() {
      const items = [
        {
          name: "Activate",
          value: "activate_action",
          icon: "icons/dropdown/activate.svg",
          order: 1,
        },
        {
          name: "Delete",
          value: "delete_action",
          icon: "icons/dropdown/delete.svg",
          negative: true,
          order: 3,
        },
      ];
      return items;
    },

    async onOptionClick(payload) {
      try {
        if (!payload || !payload.data) {
          console.warn("Invalid data passed to onOptionClick:", payload);
          return;
        }

        const row = payload.data;
        if (row.data?.type === "responsibility" && !row.data?.isSkeletonLoader) {
          const assignmentId =
            row.assignment_id ??
            (typeof row.id === "string" && row.id.startsWith("responsibility_")
              ? parseInt(row.id.replace(/^responsibility_/, ""), 10)
              : null);

          if (assignmentId != null && !Number.isNaN(assignmentId)) {
            await this.$router
              .push({
                name: "EmployeeAssignmentDetail",
                params: { assignmentId: String(assignmentId) },
                query: { tab: "Checklist" },
              })
              .catch((err) => {
                if (err.name !== "NavigationDuplicated" && err.name !== "NavigationAborted") {
                  console.error(err);
                }
              });
            return;
          }
        }

        this.loading.modal = true;
        this.currentData = row;
        this.dialog = true;
        this.selectedItemTab = 0;
      } catch (error) {
        console.error("Error in onOptionClick:", error);
        this.clearModalStates();
      } finally {
        this.loading.modal = false;
      }
    },

    async onAvatarOptionClick(data) {
      try {
        if (!data || !data.data) {
          console.warn("Invalid data passed to onAvatarOptionClick:", data);
          return;
        }

        // Hide all active tooltips before opening modal
        this.hideAllTooltips();

        this.loading.modal = true;
        this.currentData = data.data;
        this.dialog = true;
        this.selectedItemTab = 1;
      } catch (error) {
        console.error("Error in onAvatarOptionClick:", error);
        this.clearModalStates();
      } finally {
        this.loading.modal = false;
      }
    },

    onBack() {
      try {
        this.dialog = false;
        this.selectedItemTab = null;
        this.clearModalStates();

        // Hide any lingering tooltips after modal closes and prevent immediate re-showing
        this.$nextTick(() => {
          this.hideAllTooltips();
          this.temporarilyDisableTooltips();
        });
      } catch (error) {
        console.error("Error in onBack:", error);
        // Ensure modal states are cleared even if there's an error
        this.dialog = false;
        this.selectedItemTab = null;
        this.currentData = null;
        this.hideAllTooltips();
        this.temporarilyDisableTooltips();
      }
    },

    clearModalStates() {
      // Clear all modal states to prevent unwanted reopening
      this.dialog = false;
      this.selectedItemTab = null;
      this.currentData = null;
      this.checkpointsData = [];
      this.evalModalEmpState = null;
      this.evalModalHeaderState = null;
      this.currentResponsibility = null;
    },

    hideAllTooltips() {
      // Force close all Element UI tooltips by removing their DOM elements
      try {
        const tooltips = document.querySelectorAll(".el-tooltip__popper");
        tooltips.forEach((tooltip) => {
          tooltip.style.display = "none";
        });

        // Trigger mouseleave on all tooltip trigger elements (avatars, buttons, etc.)
        const tooltipTriggers = document.querySelectorAll(".el-tooltip");
        tooltipTriggers.forEach((trigger) => {
          const event = new MouseEvent("mouseleave", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          trigger.dispatchEvent(event);
        });

        // Also dispatch on the entire document to catch any remaining tooltips
        const bodyEvent = new MouseEvent("mouseleave", {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        document.body.dispatchEvent(bodyEvent);
      } catch (error) {
        console.error("Error hiding tooltips:", error);
      }
    },

    temporarilyDisableTooltips() {
      // Temporarily disable pointer events on avatar containers to prevent tooltip re-showing
      try {
        // Find all avatar containers in the Available Inspectors column
        const avatarContainers = document.querySelectorAll(
          ".people-button, .no-hover-people-button"
        );

        // Store original pointer events values
        const originalPointerEvents = [];

        avatarContainers.forEach((container, index) => {
          originalPointerEvents[index] = container.style.pointerEvents;
          container.style.pointerEvents = "none";
        });

        // Re-enable pointer events after 500ms (enough time for user to move mouse)
        setTimeout(() => {
          avatarContainers.forEach((container, index) => {
            container.style.pointerEvents = originalPointerEvents[index] || "";
          });
        }, 500);
      } catch (error) {
        console.error("Error temporarily disabling tooltips:", error);
      }
    },

    async handlePaginationRequest(paginationData) {
      // Calculate offset from page and limit [[memory:5237252]]
      this.pagination.page = paginationData.page;
      this.pagination.offset = (paginationData.page - 1) * this.pagination.limit;

      try {
        this.loading.pagination = true;
        this.$loader.show();
        await this.getEmployeesAssignments();
      } catch (error) {
        console.error("Error loading paginated data:", error);
      } finally {
        this.loading.pagination = false;
        this.$loader.hide();
      }
    },

    async onTreeExpandRole({ assignmentId, rowType, expandedRowData }) {
      if (this.useStaticPathToReadinessData) {
        return;
      }
      const accountID = this.accountID;
      const empId = resolveStoredAuthEmployeeId(this.$route.query.employeeId);

      // Check if data is already loaded for this expansion
      const cacheKey = `${rowType}_${assignmentId}`;
      if (this.loadedChildData.has(cacheKey)) {
        console.log(`Data already loaded for ${cacheKey}, skipping API call`);
        return;
      }

      this.removeSkeletonLoaders(assignmentId);

      try {
        if (rowType === "position") {
          await this.handlePositionExpansion(accountID, empId, assignmentId);
          // Mark this position's roles as loaded
          this.loadedChildData.set(cacheKey, true);
        } else if (rowType === "role") {
          await this.handleRoleExpansion(accountID, empId, assignmentId, expandedRowData);
          // Mark this role's responsibilities as loaded
          this.loadedChildData.set(cacheKey, true);
        }
      } catch (error) {
        console.error("Error loading assignments:", error);
        this.removeSkeletonLoaders(assignmentId);
      }
    },

    async handlePositionExpansion(accountID, empId, positionAssignmentId) {
      const positionKey = `position_${positionAssignmentId}`;

      // Treat Role rows and direct Responsibility rows as one set of Position
      // children, so a re-expand does not duplicate either kind (ER-11413).
      const existingChildren = this.dataSource.filter(
        (item) =>
          item.parentId === positionKey &&
          (item.data?.type === "role" || item.data?.type === "responsibility") &&
          !item.data?.isSkeletonLoader
      );

      if (existingChildren.length > 0) {
        console.log(`Children already exist for position ${positionAssignmentId}`);
        return;
      }

      const positionData = this.dataSource.find((item) => item.id === positionKey);
      const roleCount = positionData?.data?.roleCount || 0;
      // The direct-attached sibling fetch is gated by idea-MDP-585 (ER-11414).
      // When the flag is off the count is 0 and the method behaves exactly as
      // the pre-ER-11414 role-only expansion.
      const directResponsibilityCount = this.directResponsibilityTreeEnabled
        ? positionData?.data?.directResponsibilityCount || 0
        : 0;
      const childCount = roleCount + directResponsibilityCount;

      this.addSkeletonLoaders(positionKey, "position", childCount || 2);

      // Fire the Role fetch always (existing behaviour). The direct-sourced
      // fetch is fired only when the Position listing signalled direct rows
      // (ER-11412 field on the payload): on the pre-ER-11412 API the
      // responsibility-assignment endpoint has no position_assignment filter,
      // so an unconditional call would return every employee Responsibility
      // and duplicate the Role children (ER-11413 regression guard).
      const [roleResponse, directResponse] = await Promise.all([
        getRoleAssignment(accountID, empId, positionAssignmentId, "active"),
        directResponsibilityCount > 0
          ? this.fetchDirectResponsibilityAssignments(accountID, empId, positionAssignmentId)
          : Promise.resolve(null),
      ]);

      this.removeSkeletonLoaders(positionKey);

      const today = new Date();
      const transformedChildren = [];

      const roleResults = roleResponse?.data?.results || [];
      const directResults = directResponse?.data?.results || [];

      if (roleResults.length > 0 || directResults.length > 0) {
        // Rule 12: Role rows and direct Responsibility rows are siblings under
        // the Position; the shared tree list sorts each parent's children by
        // importance (Rule 12a).
        transformedChildren.push(
          ...roleResults.map((role) => this.transformRoleData(role, positionKey, today)),
          ...directResults.map((resp) =>
            this.transformDirectResponsibilityDataFromAPI(resp, positionAssignmentId, today)
          )
        );

        this.dataSource = [...this.dataSource, ...transformedChildren];

        const positionIndex = this.dataSource.findIndex((item) => item.id === positionKey);
        if (positionIndex !== -1) {
          this.dataSource[positionIndex].data.hasRoles = roleResults.length > 0;
          this.dataSource[positionIndex].data.roleCount = roleResults.length;
          this.dataSource[positionIndex].data.hasDirectResponsibilities = directResults.length > 0;
          this.dataSource[positionIndex].data.directResponsibilityCount = directResults.length;
          console.log("Path to Readiness Data (updated with children):", this.dataSource);
        }
      }
    },

    // Direct-attached (Position-level) Responsibility Assignments come from the
    // ER-11412 backend surface. The endpoint may not be deployed yet on every
    // environment, so degrade to "no direct rows" instead of failing the whole
    // Position expansion when the call is not available. Rule 1 partitions the
    // two shapes by role_assignment null, which serializes as role_name/role_code
    // null — keep only those rows so an endpoint that has not yet added the
    // position_assignment filter can never leak Role-sourced rows in here.
    async fetchDirectResponsibilityAssignments(accountID, empId, positionAssignmentId) {
      try {
        const response = await getDirectResponsibilityAssignment(
          accountID,
          empId,
          positionAssignmentId,
          "active"
        );
        const directResults = (response?.data?.results || []).filter(
          (row) => row.role_name == null && row.role_code == null
        );
        return { data: { results: directResults } };
      } catch (error) {
        console.error("Error fetching direct responsibility assignments:", error);
        return { data: { results: [] } };
      }
    },

    async handleRoleExpansion(accountID, empId, roleAssignmentId, expandedRowData) {
      const existingResponsibilities = this.dataSource.filter(
        (item) =>
          item.parentId === expandedRowData.id &&
          item.data?.type === "responsibility" &&
          !item.data?.isSkeletonLoader
      );

      if (existingResponsibilities.length > 0) {
        console.log(`Responsibilities already exist for role ${roleAssignmentId}`);
        return;
      }

      const responsibilityCount = expandedRowData.data?.responsibilityCount || 3; // fallback to 3 if not found

      this.addSkeletonLoaders(expandedRowData.id, "role", responsibilityCount);

      const response = await getResponsibilityAssignment(
        accountID,
        roleAssignmentId,
        "active",
        "active"
      );

      this.removeSkeletonLoaders(expandedRowData.id);

      if (response.data && response.data.results && response.data.results.length > 0) {
        const today = new Date();
        const responsibilityData = response.data.results;

        const transformedResponsibilities = responsibilityData.map((resp) =>
          this.transformResponsibilityDataFromAPI(resp, expandedRowData.id, roleAssignmentId, today)
        );

        this.dataSource.push(...transformedResponsibilities);

        // Update the parent role to show it has responsibilities
        const roleIndex = this.dataSource.findIndex((item) => item.id === expandedRowData.id);
        if (roleIndex !== -1) {
          this.dataSource[roleIndex].data.hasResponsibilities = true;
          this.dataSource[roleIndex].data.responsibilityCount = transformedResponsibilities.length;
          console.log("Path to Readiness Data (updated with responsibilities):", this.dataSource);
        }
      }
    },

    transformRoleData(role, parentId, today) {
      const isRoleNew =
        role.assigned_on &&
        new Date(role.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      return {
        id: `role_${role.id}`, // Make role IDs unique
        parentId: parentId, // Set parent ID to main object
        data: {
          type: role.assignment_type || "role",
          commitment: role.commitment_level || "",
          assignmentType: role.assignment_type || "role",
          importanceLevel: role.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: this.extractCode(role.role_code) || "",
          name: role.role_name || "",
          isNew: isRoleNew || false,
          isAccepted: isRoleNew || false,
          acceptanceStatus: "pending",
          assignment_id: parentId,
          // Add expandable indicator if responsibilities exist
          hasResponsibilities: role.responsibility_assignment_count > 0,
          responsibilityCount: role.responsibility_assignment_count,
        },
        assignment_id: role.id,
        readiness: role.current_readiness || 0,
      };
    },

    transformResponsibilityDataFromAPI(resp, parentId, assignmentId, today) {
      const checkpoints = [];
      const isRespNew =
        resp.assigned_on &&
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      let badge = null;
      if (resp.badge_level !== null && resp.badge_status !== "expired") {
        badge = {
          id: resp.badge_id,
          level: resp.badge_level,
          status: resp.badge_issued_method,
          expiration_date: resp.badge_expiration_date,
          issued_method: resp.badge_issued_method,
        };
      }

      // Extract checkpoint counts from API response
      const checkpointCounts = {
        apprentice_checkpoint_count: resp.apprentice_checkpoint_count || 0,
        professional_checkpoint_count: resp.professional_checkpoint_count || 0,
        coach_checkpoint_count: resp.coach_checkpoint_count || 0,
        master_checkpoint_count: resp.master_checkpoint_count || 0,
        apprentice_passed_checkpoint_count: resp.apprentice_passed_checkpoint_count || 0,
        professional_passed_checkpoint_count: resp.professional_passed_checkpoint_count || 0,
        coach_passed_checkpoint_count: resp.coach_passed_checkpoint_count || 0,
        master_passed_checkpoint_count: resp.master_passed_checkpoint_count || 0,
      };

      return {
        id: `responsibility_${resp.id}`, // Make responsibility IDs unique
        parentId: parentId, // Correctly set parent ID
        assignment_id: resp.id,
        data: {
          type: resp.assignment_type || "responsibility",
          commitment: resp.commitment_level || "",
          assignmentType: resp.assignment_type || "responsibility",
          importanceLevel: resp.importance || 0,
          importanceSelector: true,
          totalSteps: 3,
          code: this.extractCode(resp.responsibility_code) || "",
          name: resp.responsibility_name || "",
          isNew: isRespNew || false,
          isAccepted: isRespNew || false,
          acceptanceStatus: "pending",
          assignment_id: assignmentId,
          badge: badge,
          responsibility_id: resp.responsibility,
          checkpointCounts: checkpointCounts,
          avatars: (resp.available_inspectors || []).map((inspector) => {
            // Split full_name into firstName and lastName
            const nameParts = (inspector.full_name || "").trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            // Try to find badge level from badge_holders by matching name
            const badgeHolder = (resp.badge_holders || []).find((bh) => {
              const badgeHolderFullName = `${bh.first_name || ""} ${bh.last_name || ""}`.trim();
              return badgeHolderFullName === (inspector.full_name || "").trim();
            });

            return {
              id: inspector.id,
              badgeLevel: badgeHolder?.badge_level || null,
              firstName: firstName,
              lastName: lastName,
              fullName: inspector.full_name || "",
              title: badgeHolder?.title || null,
              master_will_coach: badgeHolder?.master_will_coach || null,
              img: inspector.avatar || "",
              isHoverEnabled: !!inspector.avatar,
            };
          }),
        },
        checkpointsData: checkpoints,
        status: resp.status || "active",
        readiness: resp.current_readiness || 0,
      };
    },

    transformDirectResponsibilityDataFromAPI(resp, positionAssignmentId, today) {
      // Rule 12: a direct-sourced Responsibility Assignment hangs straight off
      // the Position (parentId position_<id>) — no Role in between. Reuse the
      // standard Responsibility row transform so the card renders identically
      // (checkpoints/badges/inspectors), then relabel the linkage and mark the
      // row so click-through navigation can stop at the Position.
      const row = this.transformResponsibilityDataFromAPI(
        resp,
        `position_${positionAssignmentId}`,
        positionAssignmentId,
        today
      );
      row.data.directResponsibility = true;
      return row;
    },

    addSkeletonLoaders(parentId, rowType, count = null) {
      // Check if skeleton loaders already exist for this parent
      const existingSkeletons = this.dataSource.filter(
        (item) => item.parentId === parentId && item.data?.isSkeletonLoader
      );

      if (existingSkeletons.length > 0) {
        return; // Skip adding if already exists
      }

      // Use provided count or fallback to default values
      const skeletonCount = count || (rowType === "position" ? 2 : 3);
      const skeletonItems = [];

      for (let i = 0; i < skeletonCount; i++) {
        const skeletonId = `skeleton-${parentId}-${i}`;
        skeletonItems.push({
          id: skeletonId,
          parentId: parentId,
          data: {
            type: "skeleton",
            assignmentType: rowType === "position" ? "role" : "responsibility",
            skeletonFor: rowType,
            isSkeletonLoader: true,
            commitment: "",
            importanceLevel: 0,
            importanceSelector: false,
            totalSteps: 3,
            code: "",
            name: "",
            isNew: false,
            isAccepted: false,
            acceptanceStatus: "pending",
          },
          readiness: 0,
          status: "loading",
        });
      }

      // Add skeleton items to dataSource
      this.dataSource = [...this.dataSource, ...skeletonItems];
    },

    removeSkeletonLoaders(parentId) {
      // Remove skeleton items for this parent
      this.dataSource = this.dataSource.filter((item) => {
        const isSkeletonForParent = item.id.toString().startsWith(`skeleton-${parentId}-`);
        const isSkeletonLoader = item.data?.isSkeletonLoader;
        const hasCorrectParent = item.parentId === parentId;

        // Remove if it's a skeleton loader for this parent
        return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
      });
    },

    handleExpansionStateChange(expandedKeys) {
      // Update our local copy of expanded keys when user manually expands/collapses
      this.expandedKeys = expandedKeys || [];
    },

    handleTabSelected(tab) {
      const tabIndexMap = {
        "Your Path to Readiness": 0,
      };

      this.selectedTab = tabIndexMap[tab] || 0;
    },
  },
};
</script>

<style lang="css" scoped>
.readiness-container {
  margin: 15px;
  padding-top: 15px;
  width: 100%;
}

.employee-path {
  border-radius: 8px;
}
::v-deep .progress-bar-container {
  width: 100%;
}
.employee-readiness-container {
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow: none;
  box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.1), 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

#treelist {
  max-width: 100%;
}

::v-deep(.v-tabs) {
  padding: 13px 0px !important;
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep(.custom-tabs) {
  top: 0px;
}

::v-deep(.container) {
  padding: 0px !important;
}

.close-btn-container {
  position: absolute;
  right: 25px;
  margin-top: 10px;
}

.close-btn {
  position: absolute;
}

::v-deep(.empty-background-pattern) {
  padding-top: 0px !important;
}

::v-deep(.table-content > div:first-child) {
  margin-bottom: 4px !important;
}
::v-deep .employee-readiness .new-progress-bar-container {
  margin-left: 5px;
}

.as-employee-legacy-tree ::v-deep .ex-pathtoread-treelist {
  width: 100%;
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  display: flex;
  flex-direction: column;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-headers .dx-treelist-table .dx-row > td {
  background-color: #f9fafb;
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  padding: 12px 16px;
  border-bottom: 1px solid #eaecf0;
  text-transform: capitalize;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row {
  border-bottom: 1px solid #eaecf0;
  height: 72px;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row > td {
  padding: 16px 16px;
  vertical-align: middle;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row:hover,
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-selection-row {
  background-color: #f9fafb !important;
}
.as-employee-legacy-tree ::v-deep .assignment-card .name-container,
.as-employee-legacy-tree ::v-deep .assignment-card .name-container .org-object-card-name,
.as-employee-legacy-tree ::v-deep .assignment-card .name-container .name-text {
  font-weight: 700 !important;
  color: #101828 !important;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-content,
.as-employee-legacy-tree ::v-deep .dx-treelist-borders {
  border: none !important;
}

.as-employee-legacy-tree ::v-deep .dx-treelist-table {
  table-layout: fixed !important;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-headers .dx-treelist-table .dx-row > td:first-child,
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row > td:first-child {
  width: 600px !important;
  min-width: 600px !important;
  max-width: 600px !important;
}
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row > td:first-child .ml-4,
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row > td:first-child .assignment-card {
  max-width: 350px;
}
.as-employee-legacy-tree.flag-on ::v-deep .dx-row > td:first-child .ml-4,
.as-employee-legacy-tree.flag-on ::v-deep .dx-row > td:first-child .assignment-card {
  max-width: 100% !important;
}
.as-employee-legacy-tree ::v-deep .assignment-card,
.as-employee-legacy-tree ::v-deep .assignment-card .sub-div,
.as-employee-legacy-tree ::v-deep .assignment-card .code-name-container {
  min-width: 0;
  overflow: hidden;
}
.as-employee-legacy-tree ::v-deep .assignment-card .url-name,
.as-employee-legacy-tree ::v-deep .assignment-card .position-name {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100% !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.as-employee-legacy-tree ::v-deep .dx-treelist-headers .dx-treelist-table .dx-row > td:nth-child(4),
.as-employee-legacy-tree ::v-deep .dx-treelist-rowsview .dx-row > td:nth-child(4) {
  box-sizing: border-box !important;
  width: 150px !important;
  min-width: 150px !important;
  max-width: 150px !important;
}
</style>
